import * as React from 'react';

import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion';

type UseCountUpOptions = {
  target: number;
  active: boolean;
  durationMs?: number;
  decimals?: number;
};

/**
 * Count from 0 → target when `active` becomes true.
 * Skips animation when reduced motion is preferred.
 */
export function useCountUp({
  target,
  active,
  durationMs = 1400,
  decimals = 0
}: UseCountUpOptions): number {
  const reduced = usePrefersReducedMotion();
  const [value, setValue] = React.useState(0);
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    if (!active) return;

    if (reduced) {
      setValue(target);
      startedRef.current = true;
      return;
    }

    // Avoid restarting the animation if reduced-motion settles from true → false after mount.
    if (startedRef.current) return;
    startedRef.current = true;

    let frame = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / durationMs);
      // Ease-out cubic for a confident settle.
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = target * eased;
      setValue(decimals > 0 ? Number(next.toFixed(decimals)) : Math.round(next));

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, decimals, durationMs, reduced, target]);

  return value;
}
